import { toast } from "sonner";
import { ProgressToast } from "../components/progress-toast";

interface ProgressToastHandle {
  /** Update progress (0-100). Pass a message to change the label. */
  update: (progress: number, message?: string) => void;
  /** Transition to success state. Toast auto-dismisses after default duration. */
  success: (message: string) => void;
  /** Transition to error state. Toast auto-dismisses after default duration. */
  error: (message: string) => void;
  /** Dismiss the toast immediately. */
  dismiss: () => void;
}

/**
 * Create a persistent progress toast for downloads/exports.
 *
 * @param message - Initial message shown in the toast
 * @param options.indeterminate - Show indeterminate progress bar (no percentage)
 * @returns Handle to update, complete, or dismiss the toast
 *
 * @example
 * ```ts
 * const progress = createProgressToast("Downloading...");
 * progress.update(50);
 * progress.success("Download complete");
 * ```
 *
 * @example
 * ```ts
 * // Indeterminate mode for operations without progress tracking
 * const progress = createProgressToast("Exporting CSV...", { indeterminate: true });
 * progress.success("Export complete");
 * ```
 */
export function createProgressToast(message: string, options: { indeterminate?: boolean } = {}): ProgressToastHandle {
  let isIndeterminate = options.indeterminate ?? false;
  let currentMessage = message;

  const id = toast.custom(
    () => <ProgressToast message={currentMessage} progress={0} indeterminate={isIndeterminate} />,
    { duration: Infinity }
  );

  return {
    update(progress: number, newMessage?: string) {
      if (newMessage) currentMessage = newMessage;
      // Switch to determinate mode once meaningful progress is reported
      if (progress > 0) isIndeterminate = false;
      toast.custom(
        () => <ProgressToast message={currentMessage} progress={progress} indeterminate={isIndeterminate} />,
        { id, duration: Infinity }
      );
    },
    success(successMessage: string) {
      toast.success(successMessage, { id, duration: 4000 });
    },
    error(errorMessage: string) {
      toast.error(errorMessage, { id, duration: 4000 });
    },
    dismiss() {
      toast.dismiss(id);
    }
  };
}
